import type { GoogleSettings } from "@dentacore/shared-types";

import { api } from "./client";

/**
 * Google Calendar sync. **Clinic-scoped and admin only** — a clinic's Google
 * account covers every branch it runs (G2), so none of these depends on which
 * branch is selected.
 *
 * Every write returns the whole settings payload rather than just what it
 * changed: pairing touches every chair at once, so a caller merging a narrower
 * response would have to guess at the rest.
 */

export function readGoogleSettings(signal?: AbortSignal): Promise<GoogleSettings> {
  return api<GoogleSettings>("/google/settings", { signal });
}

/**
 * Set the practice's Google account and pair every chair.
 *
 * **Nothing syncs when this returns.** Every chair lands in
 * `AWAITING_CONFIRMATION`, because Google returns 200 for a grant to an address
 * that does not exist and there is no way to tell from here.
 */
export function setGoogleAddress(email: string): Promise<GoogleSettings> {
  return api<GoogleSettings>("/google/address", { method: "POST", body: { email } });
}

/** The only check that a pairing actually landed: a human says they can see it. */
export function confirmGoogleChair(chairId: string): Promise<GoogleSettings> {
  return api<GoogleSettings>(`/google/chairs/${chairId}/confirm`, { method: "POST" });
}

/** Stop syncing, keeping the calendar with the practice (G18). */
export function unpairGoogleChair(chairId: string): Promise<GoogleSettings> {
  return api<GoogleSettings>(`/google/chairs/${chairId}/unpair`, { method: "POST" });
}

/** Pair a chair that predates the clinic's Google setup, or was unpaired. */
export function pairGoogleChair(chairId: string): Promise<GoogleSettings> {
  return api<GoogleSettings>(`/google/chairs/${chairId}/pair`, { method: "POST" });
}
